// The shared DIAGNOSTIC CONTROLS strip + lens tabs. The strip itself (header, Message Center, Call
// Details line, and the Import/Run/Stop/Clear/Download buttons) is STATIC markup in the shell
// (#call-bar); those controls are wired by main.ts by id and work from any of the three One-Off
// lenses. This module only: shows/hides the strip per view, renders + syncs the lens tabs, and keeps
// the one-line Call Details summary current. No slide animation — the strip is fixed above the tabs.
//
// Model-derived text (call type, buyer/seller names) is written via textContent — never innerHTML.

import { onViewChange } from './sidenav.js';
import { paintRepClass } from './repClass.js';
import type { ImportResponse } from '../lib/types.js';

/** The three single-call lenses, in tab order. `view` is the existing data-view key (unchanged). */
export const LENSES: ReadonlyArray<{ view: string; label: string }> = [
  { view: 'diagnostic', label: 'Quick Snapshot' },
  { view: 'cross-skill', label: 'Cross-Skill Diagnostic' },
  { view: 'buyer-side', label: 'Buyer Signals' },
  { view: 'meddpicc', label: 'MEDDPICC' },
];
const LENS_VIEWS = new Set(LENSES.map((l) => l.view));

/** Views that carry the shared strip but are not one of the four single-call LENSES. Coaching Summary
 *  was split out of Quick Snapshot onto its own page; it reads the same loaded call, so it needs the
 *  same Import / Run / Clear / PDF controls and Call Details above it. Kept separate from LENSES so
 *  that list keeps meaning "the four lenses". */
const EXTRA_STRIP_VIEWS: ReadonlyArray<{ view: string; label: string }> = [
  { view: 'coaching', label: 'Coaching Summary' },
];
const STRIP_TITLES = new Map([...LENSES, ...EXTRA_STRIP_VIEWS].map((l) => [l.view, l.label]));

/** Pure visibility rule (unit-tested): the shared strip shows on the lens views, on Coaching Summary,
 *  AND on Quick Snapshot (diagnostic) — with or without a loaded call — and never on Home, Account,
 *  the cohort, etc. Quick Snapshot now reuses this same #call-bar as its right column (score card +
 *  controls), so the two pages render the identical element; its legacy in-page .sk-side is hidden. */
export function callBarVisible(activeView: string): boolean {
  return LENS_VIEWS.has(activeView) || STRIP_TITLES.has(activeView);
}

export interface CallBarState {
  session: ImportResponse | null;
  hasContent: boolean;
  busy: boolean;
  /** True once the call has been scored (Run finished). Before that the buyer name is withheld. */
  scored: boolean;
  /** True while the Buyer Signals analysis is running (kept for callers; not read here). */
  buyerRunning: boolean;
  /** The imported transcript's file name, shown as a second Call Details line (matches Quick Snapshot). */
  fileName: string;
}

export interface CallBarDeps {
  container: HTMLElement;
  getState: () => CallBarState;
  /** After the one-line details render, the host may append extra controls into the same element (the
   *  interactive "Taken / Set Timestamp" line), so the lens rail matches Quick Snapshot exactly. */
  onDetailsRendered?: (detailsEl: HTMLElement) => void;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string): HTMLElementTagNameMap[K] {
  const n = document.createElement(tag);
  if (cls) n.className = cls;
  if (text !== undefined) n.textContent = text;
  return n;
}

/** Format a call time as "Mar 12, 2026, 9:30 AM". Rendered in UTC so the shown clock matches the naive
 *  time written in the transcript (which the parser interpreted as UTC), instead of drifting by the
 *  viewer's timezone. */
export function fmtCallTime(ms: number): string {
  return new Date(ms).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'UTC',
  });
}

/** Mount the strip controller and return a refresh handle; main.ts calls refresh() from setPhase. */
export function mountCallBar(deps: CallBarDeps): { refresh: () => void } {
  // Seed from the CURRENTLY-active view, not a hardcoded 'home': this mounts after the hard-refresh
  // view-restore has already fired onViewChange, so relying on that event alone would leave the strip
  // hidden on a lens page until the next navigation.
  let activeView = document.querySelector('.view.is-active')?.getAttribute('data-view') ?? 'home';
  const detailsEl = document.getElementById('callbar-details');
  const titleEl = document.getElementById('callbar-title');
  const classBox = document.getElementById('callbar-tb-class');
  const classVal = document.getElementById('callbar-class-val');
  // The strip no longer has its own lens tabs or collapse button. Lens navigation is the sidenav; the
  // header "Advanced" toggle reveals/hides the Message Center (main.ts toggles .cb-adv-off), matching
  // Quick Snapshot.

  // The one-line Call Details summary: call type · duration · Rep · Buyer. The buyer name is withheld
  // until the call is scored (before Run → "Pending RUN", during → "extracting info").
  const renderDetails = (st: CallBarState): void => {
    // The CLASS block (shown on every strip page) resolves the rep's class server-side by rep_key.
    paintRepClass(classBox, classVal, {
      hasCall: !!st.session,
      scored: st.scored,
      repName: st.session?.participants?.rep_name?.trim() ?? null,
    });
    if (!detailsEl) return;
    const s = st.session;
    if (!s) {
      // No transient status here — import/classify progress lives in the Message Center. The Call
      // Details line just stays empty until a call is classified.
      detailsEl.textContent = '';
      return;
    }
    detailsEl.textContent = '';
    const callType = s.call_meta?.call_type ?? 'Imported call';
    const seller = s.participants?.rep_name;
    const duration = document.getElementById('call-duration')?.textContent?.trim();
    const buyer = st.scored ? s.participants?.business_name : st.busy ? 'extracting info' : 'Pending RUN';
    detailsEl.appendChild(el('span', 'sk-cd-type', String(callType)));
    const rest: string[] = [];
    if (duration) rest.push(duration);
    if (seller) rest.push(`Rep: ${seller}`);
    if (buyer) rest.push(`Buyer: ${buyer}`);
    // When the call actually happened. "Not Available" (honest) when we could not find a date, rather
    // than a fabricated import date; the Message Center explains it and offers "Set call date".
    // The "Taken: <date>" line + Set/Edit Timestamp control is appended by the host via onDetailsRendered
    // (the same appendCallDate as Quick Snapshot), so the rail's timestamp is interactive and shared.
    if (rest.length) detailsEl.appendChild(document.createTextNode('  ·  ' + rest.join('  ·  ')));
    // The imported transcript's file name, on its own muted second line (matches Quick Snapshot).
    if (st.fileName) {
      const file = document.createElement('div');
      file.className = 'sk-cd-file';
      const lbl = document.createElement('span');
      lbl.className = 'sk-cd-file-label';
      lbl.textContent = 'Transcript: ';
      file.appendChild(lbl);
      file.appendChild(document.createTextNode(st.fileName));
      file.title = st.fileName;
      detailsEl.appendChild(file);
    }
    deps.onDetailsRendered?.(detailsEl);
  };

  const render = (): void => {
    if (!callBarVisible(activeView)) {
      deps.container.hidden = true;
      return;
    }
    deps.container.hidden = false;
    if (titleEl) titleEl.textContent = STRIP_TITLES.get(activeView) ?? 'Diagnostic';
    renderDetails(deps.getState());
  };

  onViewChange((v) => {
    activeView = v;
    render();
  });
  return { refresh: render };
}
